
import { MessageType } from "@/components/chat/ChatMessage";
import { v4 as uuidv4 } from "uuid";
import { WebSocketMessage, StructuredWebSocketMessage, AuthenticatedUser, UnauthenticatedUser } from "./types";
import { User } from "@/context/auth/types";

// Session management
const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem('chat_session_id');
  if (!sessionId) {
    sessionId = `session-${uuidv4()}`;
    localStorage.setItem('chat_session_id', sessionId);
  }
  return sessionId;
};

// Simple IP detection (limited in browser environment)
const getClientIP = (): string => {
  // In browser, we can't directly get the real IP address
  // This would need to be enhanced with a service call if needed
  return "127.0.0.1"; // Fallback
};

// Generate anonymous user ID
const getOrCreateAnonymousId = (): string => {
  let anonymousId = localStorage.getItem('anonymous_user_id');
  if (!anonymousId) {
    anonymousId = `anon-${uuidv4()}`;
    localStorage.setItem('anonymous_user_id', anonymousId);
  }
  return anonymousId;
};

export const parseWebSocketMessage = (
  data: any, 
  initialMessageReceived: boolean,
  messages: MessageType[]
): {
  message?: MessageType;
  isInitialMessage: boolean;
  isError: boolean;
  errorMessage?: string;
} => {
  console.log('Parsing WebSocket message:', data);
  
  // Handle the actual server response format
  if (data.status === 'success' && data.response) {
    // Extract text content from the response object
    let messageText = '';
    
    if (typeof data.response === 'string') {
      messageText = data.response;
    } else if (data.response && typeof data.response === 'object') {
      // Try different possible text fields in order of preference
      messageText = data.response.response_text || 
                   data.response.output || 
                   data.response.message || 
                   data.response.response || 
                   'No message content';
    }
    
    return {
      message: {
        id: uuidv4(),
        content: messageText,
        role: "assistant",
        timestamp: new Date(),
        read: false,
      },
      isInitialMessage: false,
      isError: false
    };
  }

  // Handle error responses
  if (data.status === 'error') {
    return {
      isInitialMessage: false,
      isError: true,
      errorMessage: data.error || "An error occurred"
    };
  }

  // Handle config and ready messages
  if (data.type === 'ready' || data.type === 'config') {
    if (!initialMessageReceived && data.type === 'config' && data.data?.first_message) {
      return {
        message: {
          id: uuidv4(),
          content: data.data.first_message,
          role: "assistant",
          timestamp: new Date(),
          read: false,
        },
        isInitialMessage: true,
        isError: false
      };
    }
    return { isInitialMessage: false, isError: false };
  } 

  // Handle other message types for backward compatibility
  if (data.type === 'response' || data.type === 'message' || data.type === 'ai_response') {
    const messageText = data.data?.reply || 
                       data.text || 
                       data.message || 
                       (data.data && data.data.text) || 
                       (data.data && data.data.message) || 
                       'No message content';
    
    return {
      message: {
        id: uuidv4(),
        content: messageText,
        role: "assistant",
        timestamp: new Date(),
        audioUrl: data.audio_url || (data.data && data.data.audio),
        read: false,
      },
      isInitialMessage: false,
      isError: false
    };
  }
  
  return { isInitialMessage: false, isError: false };
};

export const createUserMessage = (text: string): MessageType => ({
  id: uuidv4(),
  content: text,
  role: "user",
  timestamp: new Date(),
  read: true,
});

// Enhanced message format with structured user objects
export const formatMessageForWebSocket = (
  text: string, 
  user: User | null = null,
  options: {
    contactId?: number;
    useStructuredFormat?: boolean;
    additionalMetadata?: Record<string, any>;
  } = {}
): string => {
  const { contactId, useStructuredFormat = true, additionalMetadata = {} } = options;

  // Use structured format by default
  if (useStructuredFormat) {
    const sessionId = getOrCreateSessionId();
    const metadata = {
      ip_address: getClientIP(),
      ...additionalMetadata
    };

    if (user) {
      // Authenticated user format
      const authenticatedUser: AuthenticatedUser = {
        id: user.id,
        role: user.role || "user",
        first_name: user.firstName || user.username || user.email || "User",
        is_internal: true
      };

      const message: StructuredWebSocketMessage = {
        input: text,
        user: authenticatedUser,
        channel: "chat",
        session_id: sessionId,
        metadata
      };

      // Add optional contact_id if provided
      if (contactId) {
        message.contact_id = contactId;
      }

      return JSON.stringify(message);
    } else {
      // Unauthenticated user format
      const unauthenticatedUser: UnauthenticatedUser = {
        id: getOrCreateAnonymousId(),
        name: "Potential Customer",
        role: "Customer",
        is_internal: false
      };

      const message: StructuredWebSocketMessage = {
        input: text,
        user: unauthenticatedUser,
        channel: "chat",
        metadata
      };

      return JSON.stringify(message);
    }
  }

  // Fallback to legacy format for backward compatibility
  let userInfo: string;
  
  if (user) {
    // For authenticated users, use firstName (coworker) format with fallbacks
    const displayName = user.firstName || user.username || user.email || "coworker";
    userInfo = `${displayName} (coworker)`;
  } else {
    // For unauthenticated users
    userInfo = "potential customer";
  }

  return JSON.stringify({ 
    input: text,
    user: userInfo,
    channel: "chat"
  });
};

// Utility function to switch between formats
export const createWebSocketMessage = (
  text: string,
  user: User | null = null,
  format: 'structured' | 'legacy' = 'structured',
  options: {
    contactId?: number;
    additionalMetadata?: Record<string, any>;
  } = {}
): string => {
  return formatMessageForWebSocket(text, user, {
    ...options,
    useStructuredFormat: format === 'structured'
  });
};
